import React, {FunctionComponent, useMemo} from 'react'
import {AdministrationLayout, ContentCard, IconContainer,} from "@biron-data/react-components";
import AdminBreadcrumb from "./Admin.Breadcrumb";
import Language from "../../language";
import {
  CalendarIcon,
  CollectionIcon,
  DatabaseIcon,
  ExternalLinkIcon,
  FolderIcon,
  UserIcon
} from "@heroicons/react/outline";
import {Col, Row} from 'antd';
import {
  buildWorkspaceAdminDatamodelsUri,
  buildWorkspaceAdminExportsUri,
  buildWorkspaceAdminPeriodsUri,
  buildWorkspaceAdminUsersUri,
  buildWorkspaceAdminWorkspacesUri,
  buildWorkspaceAdminWorkspaceUri
} from "../../commons/buildWorkspaceAdminUri";
import {useSelector} from "react-redux";
import {getCurrentWorkspaceExpanded} from "../../redux/workspace.selector";
import {useNavigate} from "react-router-dom";
import styled from "styled-components";
import {AdminCategory, useGetAdminAccess} from "../../hooks/useGetAdminAccess";
import {useFilterAvailable} from "../../hooks/useFilterAvailable";

enum ElementType {
  "menu" = "menu",
  "divider" = "divider"
}
interface Element {
  type: ElementType.menu | ElementType.divider
}

interface MenuElement extends Element {
  type: ElementType.menu
  icon: FunctionComponent
  title: string
  link: string
  isAvailable: boolean
}

interface DividerElement extends Element {
  type: ElementType.divider
}

const AdminHome = () => {
  const getAdminAccess = useGetAdminAccess()
  const workspace = useSelector(getCurrentWorkspaceExpanded)
  const navigate = useNavigate()
  const filterAvailable = useFilterAvailable()

  const elements: (MenuElement | DividerElement)[] = useMemo(() => {
    const firstPart: (MenuElement | DividerElement)[] = filterAvailable([
      {
        type: ElementType.menu,
        isAvailable: getAdminAccess(AdminCategory.workspace),
        icon: FolderIcon,
        title: Language.get("admin.home.workspace"),
        link: workspace ? buildWorkspaceAdminWorkspaceUri(workspace, `${workspace?.id}`) : ""
      },
    ])
    const secondPart: (MenuElement | DividerElement)[] = filterAvailable([
      {
        type: ElementType.menu,
        icon: UserIcon,
        isAvailable: getAdminAccess(AdminCategory.users),
        title: Language.get("admin.home.users"),
        link: workspace ? buildWorkspaceAdminUsersUri(workspace) : ""
      },
      {
        type: ElementType.menu,
        icon: DatabaseIcon,
        isAvailable: getAdminAccess(AdminCategory.datamodels),
        title: Language.get("admin.home.datamodels"),
        link: workspace ? buildWorkspaceAdminDatamodelsUri(workspace) : ""
      },
      {
        type: ElementType.menu,
        icon: CollectionIcon,
        isAvailable: getAdminAccess(AdminCategory.workspaces),
        title: Language.get("admin.home.workspaces"),
        link: workspace ? buildWorkspaceAdminWorkspacesUri(workspace) : ""
      },
    ])
    const thirdPart: (MenuElement | DividerElement)[] = filterAvailable([{
        type: ElementType.menu,
        icon: CalendarIcon,
        isAvailable: getAdminAccess(AdminCategory.periods),
        title: Language.get("admin.home.periods"),
        link: workspace ? buildWorkspaceAdminPeriodsUri(workspace) : ""
      },
      {
        type: ElementType.menu,
        icon: ExternalLinkIcon,
        isAvailable: getAdminAccess(AdminCategory.exports),
      title: Language.get("admin.home.exports"),
      link: workspace ? buildWorkspaceAdminExportsUri(workspace) : ""
  }])
    return [
      ...firstPart,
    ...(secondPart.length > 0 ? [{
      type: ElementType.divider
    } as DividerElement] : []),
      ...secondPart,
      ...(thirdPart.length > 0 ? [{
        type: ElementType.divider
      } as DividerElement] : []),
      ...thirdPart
    ]
  }, [filterAvailable, getAdminAccess, workspace])

  return <AdministrationLayout breadcrumb={<AdminBreadcrumb />} alignment={"start"}>
    <ContentCard title={Language.get("admin.title")} width={"30%"} actions={[]}>
      <FlexContainer>
        {elements.map((element, i) => element.type === "divider" ? <Divider key={`divider-${i}`}></Divider> : <ClickableRow key={element.title} onClick={() => {
          navigate(element.link)
        }}>
          <Col span={4}>
            <IconContainer clickable={true} color={"var(--primary)"}><element.icon/></IconContainer>
          </Col>
          <Col span={20}>
            {element.title}
          </Col>
        </ClickableRow>)}
      </FlexContainer>
    </ContentCard>
  </AdministrationLayout>
}

export default AdminHome


const FlexContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const ClickableRow = styled(Row)`
  :hover {
      color: var(--primary);
      cursor: pointer;
  }
`

const Divider = styled.div`
  border: 1px solid var(--light-grey-2);
  `